/**
 * ShareScribeMilestone.jsx — one-tap "share my Scribe milestone" card.
 *
 * Feb 12 2026 user spec ("wire enhancement"):
 *   Drop a styled milestone card (Scribe name + level + tier + XP +
 *   words published) to X / Threads / native share sheet so every
 *   milestone shared = a free top-of-funnel ad for Smarter Learning Hub.
 *
 * Usage:
 *   <ShareScribeMilestone profile={data} open={open} onOpenChange={set} />
 *
 * Tries `navigator.share()` first (mobile native share-sheet), then
 * falls back to X intent + copy-to-clipboard. No external libs — the
 * card is rendered as a small styled div previewing exactly what the
 * shared text + URL will look like.
 */
import React, { useMemo, useState } from 'react';
import { X as XIcon, Share2, Copy, Check, Sparkles, PenLine, Crown } from 'lucide-react';

const APP_NAME = 'Smarter Learning Hub';
const APP_URL = 'https://smarteryou.live';

const ShareScribeMilestone = ({ profile, open, onOpenChange }) => {
  const [copied, setCopied] = useState(false);

  const summary = useMemo(() => {
    if (!profile) return null;
    const u = profile.user || {};
    const o = profile.output || {};
    const e = profile.earnings || {};
    return {
      name: u.name || 'Scribe',
      picture: u.picture || null,
      role: u.role || 'writer',
      country: u.country || '',
      xp: Number(e.writer_xp || 0),
      level: Number(e.writer_level || 1),
      tier: e.writer_evolution || null,
      words: Number(o.words_published || 0),
      ebooks: Number(o.ebooks || 0),
      audiobooks: Number(o.audiobook_series || 0),
      bookSeries: Number(o.text_series || 0),
      cinematic: Number(o.cinematic_series || 0),
      followers: Number(o.followers || 0),
      retentionPct: Number(o.retention_pct || 0),
    };
  }, [profile]);

  if (!open || !summary) return null;

  const shareText =
    `🖋️ Just hit Scribe Level ${summary.level} on ${APP_NAME}!\n\n` +
    `${summary.xp.toLocaleString()} Scribe XP · ${summary.words.toLocaleString()} words published\n` +
    (summary.tier ? `Tier: ${summary.tier}\n` : '') +
    `Read & write with me → ${APP_URL}`;

  const twitterIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
  const threadsIntent = `https://www.threads.net/intent/post?text=${encodeURIComponent(shareText)}`;

  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${summary.name} — Scribe Lv ${summary.level}`,
          text: shareText,
          url: APP_URL,
        });
      } else {
        window.open(twitterIntent, '_blank', 'noopener');
      }
    } catch {
      /* user dismissed share sheet — no-op */
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="share-milestone-overlay"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl bg-[#0f1117] border border-white/10 max-h-[90vh] overflow-y-auto"
        data-testid="share-milestone-dialog"
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1.5">
            <Share2 className="w-3.5 h-3.5" /> Share Scribe milestone
          </p>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center"
            data-testid="share-milestone-close"
            aria-label="Close share dialog"
          >
            <XIcon className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Card preview — what users will see when the link unfurls */}
        <div className="p-5">
          <div
            className="relative overflow-hidden rounded-2xl border border-amber-500/30 p-5 bg-gradient-to-br from-[#1a1305] via-[#160c20] to-[#091020]"
            data-testid="share-milestone-card"
          >
            {/* subtle radial */}
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-12 -left-12 w-44 h-44 rounded-full bg-fuchsia-500/15 blur-3xl pointer-events-none" />

            <div className="relative flex items-center gap-3 mb-4">
              {summary.picture ? (
                <img
                  src={summary.picture}
                  alt=""
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-400/40"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-fuchsia-600 grid place-items-center">
                  <PenLine className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                  Scribe milestone 🖋️
                </p>
                <p className="text-white text-base font-black truncate">{summary.name}</p>
                {summary.country ? (
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">
                    {summary.country}
                  </p>
                ) : null}
              </div>
            </div>

            {/* Level + XP big numbers */}
            <div className="relative grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3">
                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Scribe level
                </p>
                <p className="text-3xl font-black text-white tabular-nums">{summary.level}</p>
              </div>
              <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3">
                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Scribe XP
                </p>
                <p className="text-3xl font-black text-white tabular-nums">
                  {summary.xp.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Words + breakdown */}
            <div className="relative space-y-1 mb-4">
              <p className="text-sm text-slate-200 tabular-nums">
                <span className="text-amber-300 font-bold">{summary.words.toLocaleString()}</span>{' '}
                words published
              </p>
              {summary.tier ? (
                <p className="text-[11px] text-slate-400">
                  Tier: <span className="text-amber-200 font-bold">{summary.tier}</span>
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2 mt-2">
                {summary.ebooks > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-200 font-bold uppercase tracking-wider">
                    {summary.ebooks} ebook{summary.ebooks === 1 ? '' : 's'}
                  </span>
                )}
                {summary.audiobooks > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-200 font-bold uppercase tracking-wider">
                    {summary.audiobooks} audiobook{summary.audiobooks === 1 ? '' : 's'}
                  </span>
                )}
                {summary.bookSeries > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/15 border border-indigo-400/30 text-indigo-200 font-bold uppercase tracking-wider">
                    {summary.bookSeries} book series
                  </span>
                )}
                {summary.cinematic > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/15 border border-pink-400/30 text-pink-200 font-bold uppercase tracking-wider">
                    {summary.cinematic} cinematic
                  </span>
                )}
              </div>
            </div>

            {/* footer */}
            <div className="relative pt-3 border-t border-white/10 flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">
                {APP_NAME}
              </p>
              <p className="text-[10px] text-slate-500">{APP_URL.replace(/^https?:\/\//, '')}</p>
            </div>
          </div>

          {/* The actual text body that will be shared */}
          <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-3">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">
              Share text
            </p>
            <p
              className="text-xs text-slate-300 whitespace-pre-wrap"
              data-testid="share-milestone-text"
            >
              {shareText}
            </p>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              type="button"
              onClick={handleNativeShare}
              className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm transition-colors"
              data-testid="share-milestone-native"
            >
              <Share2 className="w-4 h-4" /> Share now
            </button>
            <a
              href={twitterIntent}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm transition-colors"
              data-testid="share-milestone-twitter"
            >
              Post on <span className="font-black">X</span>
            </a>
            <a
              href={threadsIntent}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm transition-colors"
              data-testid="share-milestone-threads"
            >
              Post on Threads
            </a>
            <button
              type="button"
              onClick={handleCopy}
              className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border font-bold text-sm transition-colors ${
                copied
                  ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-200'
                  : 'bg-white/5 hover:bg-white/10 border-white/10 text-white'
              }`}
              data-testid="share-milestone-copy"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" /> Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> Copy text
                </>
              )}
            </button>
          </div>

          <p className="text-[10px] text-slate-500 text-center mt-3">
            Every share is a free signal-boost for Smarter Learning Hub. Thanks 🖋️
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShareScribeMilestone;
